import { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { landingCopy } from '../content/landing-copy';
import AppHeader from '../components/AppHeader';
import useScrollReveal from '../hooks/useScrollReveal';
import {
  EmbedDemo,
  TileStripeConnect,
  TileAvailabilityCalendar,
  TileEmbedCode,
  HowItWorksStepCard,
} from '../components/landing';

// ============================================
// Landing Page Component
// ============================================

const Landing = () => {
  const containerRef = useScrollReveal();
  const howItWorksRef = useRef<HTMLElement>(null);
  const finalCtaRef = useRef<HTMLElement>(null);

  const scrollToHowItWorks = () => {
    howItWorksRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToWaitlist = () => {
    finalCtaRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>bknd — Direct bookings on your website</title>
        <meta name="description" content="Accept direct bookings on your website. No commissions, no middlemen — just a simple widget that connects to your Stripe account." />
        <link rel="canonical" href="https://bknd.bulir.digital/" />
        <meta property="og:title" content="bknd — Direct bookings on your website" />
        <meta property="og:description" content="Accept direct bookings on your website. No commissions, no middlemen — just a simple widget that connects to your Stripe account." />
        <meta property="og:url" content="https://bknd.bulir.digital/" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="bknd" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="bknd — Direct bookings on your website" />
        <meta name="twitter:description" content="Accept direct bookings on your website. No commissions, no middlemen — just a simple widget that connects to your Stripe account." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "bknd",
          "url": "https://bknd.bulir.digital",
          "description": "Direct booking infrastructure for short-term rental hosts. Accept bookings on your own website with no commissions."
        })}</script>
      </Helmet>
      <AppHeader />

      <main ref={containerRef} data-testid="landing-container">
        {/* =========================================== */}
        {/* SECTION 1: HERO */}
        {/* =========================================== */}
        <section
          data-testid="section-hero"
          className="py-16 md:py-24"
        >
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Left: Text content */}
              <div className="max-w-xl animate-fade-in">
                <h1 className="font-display text-4xl md:text-5xl text-neutral-900 leading-[1.02] tracking-tight">
                  {landingCopy.hero.headline}
                </h1>
                <p className="text-lg md:text-xl text-neutral-500 mt-3">
                  {landingCopy.hero.subheadline}
                </p>

                <p className="font-body text-base md:text-lg text-neutral-500 mt-5 leading-relaxed max-w-md">
                  {landingCopy.hero.bodyParagraph}
                </p>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row items-start gap-4 mt-8">
                  <Link
                    to="/waitlist"
                    className="inline-flex items-center justify-center px-7 py-3.5 bg-neutral-900 text-white font-body font-medium rounded-lg hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 transition-colors"
                  >
                    {landingCopy.hero.primaryCta}
                  </Link>
                  <button
                    onClick={scrollToHowItWorks}
                    className="inline-flex items-center font-body text-sm text-neutral-500 hover:text-neutral-700 transition-colors py-3"
                  >
                    {landingCopy.hero.secondaryCta}
                    <svg
                      className="ml-1.5 w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Trust cue */}
                <p className="text-xs text-neutral-500 mt-4">
                  {landingCopy.hero.supportingLine}
                </p>
              </div>

              {/* Right: Embedded widget demo */}
              <div
                data-testid="widget-placeholder"
                className="order-first lg:order-last"
              >
                <EmbedDemo onJoinWaitlist={scrollToWaitlist} />
                <p className="text-xs text-neutral-500 text-center mt-3">
                  {landingCopy.hero.demoHint}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================== */}
        {/* SECTION 2: HOW IT WORKS */}
        {/* =========================================== */}
        <section
          id="how-it-works"
          ref={howItWorksRef}
          data-testid="section-how-it-works"
          className="py-14 md:py-20 border-t border-neutral-100 scroll-mt-20"
        >
          <div className="max-w-6xl mx-auto px-6 scroll-reveal">
            <h2 className="font-display text-2xl md:text-3xl text-neutral-900 mb-10 md:mb-12">
              {landingCopy.howItWorks.sectionTitle}
            </h2>

            {/* 3-step card grid with micro UI tiles */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Step 1: Set up your property */}
              <div data-testid="step-1">
                <HowItWorksStepCard
                  stepNumber="01"
                  title={landingCopy.howItWorks.steps[0].title}
                  description={landingCopy.howItWorks.steps[0].description}
                  tile={<TileAvailabilityCalendar />}
                />
              </div>

              {/* Step 2: Embed and test */}
              <div data-testid="step-2">
                <HowItWorksStepCard
                  stepNumber="02"
                  title={landingCopy.howItWorks.steps[1].title}
                  description={landingCopy.howItWorks.steps[1].description}
                  tile={<TileEmbedCode />}
                />
              </div>

              {/* Step 3: Go live */}
              <div data-testid="step-3">
                <HowItWorksStepCard
                  stepNumber="03"
                  title={landingCopy.howItWorks.steps[2].title}
                  description={landingCopy.howItWorks.steps[2].description}
                  tile={<TileStripeConnect />}
                />
              </div>
            </div>

            {/* Supporting lines */}
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-14 pt-8 border-t border-neutral-100">
              {landingCopy.howItWorks.supportingLines.map((line, index) => (
                <p key={index} className="text-sm text-neutral-400 font-body">
                  {line}
                </p>
              ))}
            </div>
          </div>
        </section>

        {/* =========================================== */}
        {/* SECTION 3: KEY BENEFITS */}
        {/* =========================================== */}
        <section
          data-testid="section-key-benefits"
          className="py-14 md:py-20 border-t border-neutral-100"
        >
          <div className="max-w-6xl mx-auto px-6 scroll-reveal">
            <h2 className="font-display text-2xl md:text-3xl text-neutral-900 mb-10 md:mb-12">
              {landingCopy.keyBenefits.sectionTitle}
            </h2>

            {/* Benefits - headline + supporting line */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-10 md:gap-y-12 gap-x-16 md:gap-x-24">
              {landingCopy.keyBenefits.benefits.map((benefit, index) => (
                <div key={index} data-testid={`benefit-${index + 1}`}>
                  <h3 className="font-display text-xl md:text-2xl text-neutral-900 leading-snug">
                    {benefit.title}
                  </h3>
                  <p className="mt-2 text-sm md:text-base font-body text-neutral-500 leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* =========================================== */}
        {/* SECTION 4: WHO IT'S FOR */}
        {/* =========================================== */}
        <section
          data-testid="section-who-its-for"
          className="py-14 md:py-20 bg-neutral-50"
        >
          <div className="max-w-6xl mx-auto px-6 scroll-reveal">
            <h2 className="font-display text-2xl md:text-3xl text-neutral-900 mb-10 md:mb-12">
              {landingCopy.whoItsFor.sectionTitle}
            </h2>

            {/* Two-column comparison */}
            <div className="grid md:grid-cols-2 gap-12 md:gap-20">
              {/* For list */}
              <div data-testid="for-list">
                <h3 className="text-base font-semibold text-neutral-900 mb-6 font-body tracking-wide">
                  {landingCopy.whoItsFor.forList.title}
                </h3>
                <ul className="space-y-4">
                  {landingCopy.whoItsFor.forList.items.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 text-base text-neutral-700 leading-relaxed font-body"
                    >
                      <svg
                        className="w-5 h-5 text-neutral-900 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not for list */}
              <div data-testid="not-for-list">
                <h3 className="text-base font-semibold text-neutral-900 mb-6 font-body tracking-wide">
                  {landingCopy.whoItsFor.notForList.title}
                </h3>
                <ul className="space-y-4">
                  {landingCopy.whoItsFor.notForList.items.map((item, index) => (
                    <li
                      key={index}
                      className="flex items-start gap-3 text-base text-neutral-700 leading-relaxed font-body"
                    >
                      <svg
                        className="w-5 h-5 text-neutral-400 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Closing line */}
            <p className="text-sm text-neutral-400 mt-12 font-body">
              {landingCopy.whoItsFor.closingLine}
            </p>
          </div>
        </section>

        {/* =========================================== */}
        {/* SECTION 5: FINAL CTA */}
        {/* =========================================== */}
        <section ref={finalCtaRef} data-testid="section-final-cta" className="py-16 md:py-24">
          <div className="max-w-6xl mx-auto px-6 scroll-reveal">
            <div className="text-center max-w-lg mx-auto">
              <h2 className="font-display text-2xl md:text-3xl text-neutral-900 tracking-tight">
                {landingCopy.finalCta.headline}
              </h2>
              <p className="text-lg md:text-xl font-body text-neutral-500 mt-4">
                {landingCopy.finalCta.supportingLine1}
              </p>
              <p className="text-lg md:text-xl font-body text-neutral-500">
                {landingCopy.finalCta.supportingLine2}
              </p>
              <div className="mt-8">
                <Link
                  to="/waitlist"
                  className="inline-flex items-center justify-center px-7 py-3.5 bg-neutral-900 text-white font-body font-medium rounded-lg hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 transition-colors"
                >
                  {landingCopy.finalCta.primaryCta}
                </Link>
              </div>
              <p className="text-sm font-body text-neutral-400 mt-5">
                {landingCopy.finalCta.smallText}
              </p>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Landing;
